import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '../types'
import { AUTH_SERVICE_URL } from '../lib/constants'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  token: string | null
  setUser: (user: User | null) => void
  setToken: (token: string | null) => void
  checkAuth: () => Promise<void>
  logout: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      token: null,

      setUser: (user) =>
        set({ user, isAuthenticated: !!user, isLoading: false }),

      setToken: (token) => set({ token }),

      checkAuth: async () => {
        try {
          // Check for auth callback params from auth.omattic.com
          const urlParams = new URLSearchParams(window.location.search)
          const authToken = urlParams.get('auth_token')
          const authSuccess = urlParams.get('auth_success')
          
          if (authSuccess === 'true' && authToken) {
            // Save token to localStorage and state
            localStorage.setItem('auth_token', authToken)
            set({ token: authToken })
            
            // Extract user data from URL params
            const userEmail = urlParams.get('user_email')
            const userName = urlParams.get('user_name')
            const userId = urlParams.get('user_id')
            
            if (userEmail && userName && userId) {
              const user = {
                id: userId,
                email: decodeURIComponent(userEmail),
                name: decodeURIComponent(userName),
                role: userEmail === 'guerrerocarlos@gmail.com' ? 'admin' : 'user',
              } as User
              
              set({
                user,
                isAuthenticated: true,
                isLoading: false,
                token: authToken,
              })
              
              // Clean up URL
              window.history.replaceState({}, '', window.location.pathname)
              return
            }
          }

          // Check for stored token
          const storedToken = get().token || localStorage.getItem('auth_token')
          if (storedToken) {
            set({ token: storedToken, isAuthenticated: true, isLoading: false })
            return
          }

          // Try cookie-based auth
          const response = await fetch(`${AUTH_SERVICE_URL}/verify`, {
            credentials: 'include',
          })

          if (response.ok) {
            const data = await response.json()
            if (data.authenticated && data.user) {
              set({
                user: data.user,
                isAuthenticated: true,
                isLoading: false,
              })
              return
            }
          }

          set({ user: null, isAuthenticated: false, isLoading: false, token: null })
        } catch (error) {
          console.error('Auth check failed:', error)
          set({ user: null, isAuthenticated: false, isLoading: false, token: null })
        }
      },

      logout: async () => {
        try {
          await fetch(`${AUTH_SERVICE_URL}/logout`, {
            credentials: 'include',
          })
        } catch (error) {
          console.error('Logout failed:', error)
        } finally {
          localStorage.removeItem('auth_token')
          set({ user: null, isAuthenticated: false, token: null })
          window.location.href = '/'
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        token: state.token,
      }),
    }
  )
)
